import { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-surface-1 dark:bg-dark-surface-0 px-4">
          <div className="w-full max-w-sm rounded-2xl border border-border-default bg-surface-0 p-8 shadow-md dark:border-dark-border-default dark:bg-dark-surface-1 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-danger-50 text-danger dark:bg-danger-500/10 dark:text-danger-400 mb-4">
              <AlertTriangle size={24} />
            </div>
            <h2 className="text-lg font-semibold text-text-primary dark:text-dark-text-primary">Something went wrong</h2>
            <p className="mt-2 text-sm text-text-muted">
              An unexpected error occurred. Your data is safe on your device.
            </p>
            {this.state.error && (
              <details className="mt-4 text-left">
                <summary className="text-xs text-text-muted cursor-pointer">Error details</summary>
                <pre className="mt-2 rounded-lg bg-surface-1 dark:bg-dark-surface-2 p-3 text-xs text-text-secondary dark:text-dark-text-secondary overflow-auto">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
            >
              <RefreshCw size={16} />
              Reload app
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
